import { TAction } from '../../../common/types';
import {
	IRestExercise,
	IRestExerciseAnswer,
	IRestExerciseStudent,
	IRestExerciseStudentAnswer,
	IRestExerciseType,
	EExerciseStatus,
	TExerciseContent,
	TExerciseNative,
} from '../../../common/rest/exercises/exercisesRest';

export const EXERCISES_SET_DATA = 'EXERCISES/SET_DATA';
export const EXERCISES_SET_LOAD_FAILED = 'EXERCISES/SET_LOAD_FAILED';
export const EXERCISES_SET_EXERCISE = 'EXERCISES/SET_EXERCISE';
export const EXERCISES_SET_GENERATING = 'EXERCISES/SET_GENERATING';
export const EXERCISES_SET_ANSWER = 'EXERCISES/SET_ANSWER';
export const EXERCISES_REMOVE_ANSWER = 'EXERCISES/REMOVE_ANSWER';
export const EXERCISES_CLEAR_ANSWERS = 'EXERCISES/CLEAR_ANSWERS';
export const EXERCISES_SET_STUDENTS = 'EXERCISES/SET_STUDENTS';
export const EXERCISES_SET_STATUS = 'EXERCISES/SET_STATUS';
export const EXERCISES_SET_GENERATE_FAILED = 'EXERCISES/SET_GENERATE_FAILED';
export const EXERCISES_SET_NATIVE = 'EXERCISES/SET_NATIVE';

// translation of exactly `content` into langCode; data null while loading, { items: [] } after a failure —
// nothing shown and no retry until the content or the language changes
export type TExerciseNativeState = {
	langCode: string;
	content: TExerciseContent;
	data: TExerciseNative | null;
};

export type TAudioExercisesState = {
	types: IRestExerciseType[];
	exercises: IRestExercise[];
	myAnswers: IRestExerciseAnswer[];
	canRegenerate: boolean;
	students?: IRestExerciseStudent[];
	studentAnswers?: IRestExerciseStudentAnswer[];
};

export interface IExercisesState {
	byAudioId: Record<number, TAudioExercisesState>;
	// keyed by `${audioId}:${promptId}`, true while a generate request is in flight
	generating: Record<string, boolean>;
	// same keys: the last generate failed — auto-generation stops retrying until the user asks again
	generateFailed: Record<string, boolean>;
	// true when the last load for that audioId failed — shown instead of a spinner that never stops
	loadFailed: Record<number, boolean>;
	// keyed by exerciseId
	native: Record<number, TExerciseNativeState>;
}

export interface ISetExerciseNativeAction extends TAction<typeof EXERCISES_SET_NATIVE> {
	exerciseId: number;
	native: TExerciseNativeState;
}

export interface ISetExercisesDataAction extends TAction<typeof EXERCISES_SET_DATA> {
	audioId: number;
	data: TAudioExercisesState;
}

export interface ISetExerciseAction extends TAction<typeof EXERCISES_SET_EXERCISE> {
	audioId: number;
	exercise: IRestExercise;
}

export interface ISetExerciseGeneratingAction extends TAction<typeof EXERCISES_SET_GENERATING> {
	audioId: number;
	promptId: number;
	generating: boolean;
}

export interface ISetExerciseAnswerAction extends TAction<typeof EXERCISES_SET_ANSWER> {
	audioId: number;
	answer: IRestExerciseAnswer;
}

// rolls back an optimistic answer that failed to save on the server, so the question unlocks and can be retried
export interface IRemoveExerciseAnswerAction extends TAction<typeof EXERCISES_REMOVE_ANSWER> {
	audioId: number;
	exerciseId: number;
	questionIndex: number;
}

export interface IClearExerciseAnswersAction extends TAction<typeof EXERCISES_CLEAR_ANSWERS> {
	audioId: number;
	exerciseId: number;
}

export interface ISetExerciseStudentsAction extends TAction<typeof EXERCISES_SET_STUDENTS> {
	audioId: number;
	students: IRestExerciseStudent[];
	answers: IRestExerciseStudentAnswer[];
}

export interface ISetExercisesLoadFailedAction extends TAction<typeof EXERCISES_SET_LOAD_FAILED> {
	audioId: number;
	failed: boolean;
}

export interface ISetExerciseStatusAction extends TAction<typeof EXERCISES_SET_STATUS> {
	audioId: number;
	exerciseId: number;
	status: EExerciseStatus;
}

export interface ISetExerciseGenerateFailedAction extends TAction<typeof EXERCISES_SET_GENERATE_FAILED> {
	audioId: number;
	promptId: number;
	failed: boolean;
}

export type TExercisesActions =
	| ISetExerciseNativeAction
	| ISetExerciseStatusAction
	| ISetExerciseGenerateFailedAction
	| ISetExercisesLoadFailedAction
	| ISetExercisesDataAction
	| ISetExerciseAction
	| ISetExerciseGeneratingAction
	| ISetExerciseAnswerAction
	| IRemoveExerciseAnswerAction
	| IClearExerciseAnswersAction
	| ISetExerciseStudentsAction;
